<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import LaisCrmContactsAPI from 'dashboard/api/laisCrmContacts';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisFilterButton from 'dashboard/components-next/LaisFilterButton.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import StatusTag from 'dashboard/components-next/tag/StatusTag.vue';
import CrmFiltersDrawer from '../components/CrmFiltersDrawer.vue';
import CrmSearchDialog from '../components/CrmSearchDialog.vue';
import LeadQuickView from '../components/LeadQuickView.vue';
import { trackCrmEvent, trackCrmParityEvents } from '../utils/crmTracking';
import {
  buildStageBuckets,
  formatDate,
  leadContactLine,
  leadInitials,
  leadStats,
} from '../utils/crmPageUtils';

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const DEFAULT_ACTIVITY_DAYS = '90';
const CUSTOM_ACTIVITY_PERIOD = 'custom';
const CUSTOM_PERIOD_MAX_DAYS = 365;
const LAST_ACTIVITY_DATE_FILTER = 'last_activity';
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const toDateKey = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = value => {
  if (!DATE_KEY_PATTERN.test(value || '')) return null;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const clampDate = (date, minDate, maxDate) => {
  if (date < minDate) return minDate;
  if (date > maxDate) return maxDate;
  return date;
};

const leads = ref([]);
const isLoading = ref(false);
const hasError = ref(false);
const selectedStage = ref(null);
const selectedLead = ref(null);
const showSearch = ref(false);
const showFiltersDrawer = ref(false);
const defaultFilters = () => ({
  activity_days: DEFAULT_ACTIVITY_DAYS,
  client_type: '',
  start_date: '',
  end_date: '',
});
const filters = ref(defaultFilters());
const drawerSortOrder = ref('recent_activity');
const periodOptions = ['30', '60', '90', '120', '180', '360'];
const periodSelectOptions = computed(() => [
  ...periodOptions.map(days => ({ label: days, value: days })),
  {
    label: t('LEADS.PIPELINE.CUSTOM_PERIOD'),
    value: CUSTOM_ACTIVITY_PERIOD,
  },
]);
const stageSortOptions = computed(() => [
  {
    label: t('LEADS.PIPELINE.ORDER.RECENT'),
    value: 'recent_activity',
  },
  {
    label: t('LEADS.PIPELINE.ORDER.OLDEST'),
    value: 'oldest_activity',
  },
  {
    label: t('LEADS.PIPELINE.ORDER.NAME_ASC'),
    value: 'name_asc',
  },
  {
    label: t('LEADS.PIPELINE.ORDER.NAME_DESC'),
    value: 'name_desc',
  },
]);

const stageBuckets = computed(() => buildStageBuckets(leads.value));
const stageLabels = computed(() => ({
  sem_visita: t('LEADS.PIPELINE.STAGES.NO_VISIT'),
  uma_ou_duas_visitas: t('LEADS.PIPELINE.STAGES.ONE_TWO_VISITS'),
  tres_ou_mais_visitas: t('LEADS.PIPELINE.STAGES.THREE_PLUS_VISITS'),
  proposta_em_andamento: t('LEADS.PIPELINE.STAGES.OFFER'),
  negocio_fechado: t('LEADS.PIPELINE.STAGES.CLOSED'),
}));
const stageDescriptions = computed(() => ({
  sem_visita: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.NO_VISIT'),
  uma_ou_duas_visitas: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.ONE_TWO_VISITS'),
  tres_ou_mais_visitas: t(
    'LEADS.PIPELINE.STAGE_DESCRIPTIONS.THREE_PLUS_VISITS'
  ),
  proposta_em_andamento: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.OFFER'),
  negocio_fechado: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.CLOSED'),
}));
const stageLabel = key =>
  stageLabels.value[key] || stageLabels.value.sem_visita;
const stageDescription = key =>
  stageDescriptions.value[key] || stageDescriptions.value.sem_visita;
const stageTip = key =>
  ({
    sem_visita: 'Priorize contato inicial e convide para a primeira visita.',
    uma_ou_duas_visitas: 'Valide preferências e sugira imóveis aderentes.',
    tres_ou_mais_visitas:
      'Lead recorrente: acelere proposta ou nova curadoria.',
    proposta_em_andamento: 'Acompanhe contraproposta e próximos documentos.',
    negocio_fechado: 'Mantenha o histórico organizado para pós-venda.',
  })[key] || stageDescription(key);
const activeStage = computed(
  () =>
    stageBuckets.value.find(bucket => bucket.key === selectedStage.value) ||
    stageBuckets.value[0]
);
const hasActiveFilters = computed(
  () =>
    filters.value.activity_days !== DEFAULT_ACTIVITY_DAYS ||
    Boolean(filters.value.client_type) ||
    Boolean(filters.value.start_date) ||
    Boolean(filters.value.end_date)
);
const emptyPipeline = computed(
  () =>
    !isLoading.value &&
    !hasError.value &&
    !leads.value.length &&
    !hasActiveFilters.value
);
const emptyFiltered = computed(
  () =>
    !isLoading.value &&
    !hasError.value &&
    !leads.value.length &&
    hasActiveFilters.value
);
const todayDateKey = computed(() => toDateKey(new Date()));
const customPeriodMinDate = computed(() => {
  const today = parseDateKey(todayDateKey.value);
  return today ? toDateKey(addDays(today, -CUSTOM_PERIOD_MAX_DAYS)) : '';
});
const customStartMaxDate = computed(
  () => filters.value.end_date || todayDateKey.value
);
const customStartMinDate = computed(() => {
  const endDate =
    parseDateKey(customStartMaxDate.value) || parseDateKey(todayDateKey.value);
  return endDate ? toDateKey(addDays(endDate, -CUSTOM_PERIOD_MAX_DAYS)) : '';
});
const customEndMinDate = computed(
  () => filters.value.start_date || customPeriodMinDate.value
);

const clearCustomPeriodDates = () => {
  filters.value.start_date = '';
  filters.value.end_date = '';
};

const normalizeCustomPeriodDates = () => {
  if (filters.value.activity_days !== CUSTOM_ACTIVITY_PERIOD) {
    clearCustomPeriodDates();
    return;
  }

  const today = parseDateKey(todayDateKey.value);
  if (!today) return;

  const minAllowedEnd = addDays(today, -CUSTOM_PERIOD_MAX_DAYS);
  const endDate = clampDate(
    parseDateKey(filters.value.end_date) || today,
    minAllowedEnd,
    today
  );
  const minAllowedStart = addDays(endDate, -CUSTOM_PERIOD_MAX_DAYS);
  const startDate = clampDate(
    parseDateKey(filters.value.start_date) || minAllowedStart,
    minAllowedStart,
    endDate
  );

  filters.value.start_date = toDateKey(startDate);
  filters.value.end_date = toDateKey(endDate);
};

const handlePeriodSelection = () => {
  if (filters.value.activity_days === CUSTOM_ACTIVITY_PERIOD) {
    normalizeCustomPeriodDates();
  } else {
    clearCustomPeriodDates();
  }
};

const fetchPipeline = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const params = {
      include_contact_inboxes: 'false',
      per_page: '100',
      page: '1',
      order_by: 'recent_activity',
    };
    if (filters.value.activity_days === CUSTOM_ACTIVITY_PERIOD) {
      normalizeCustomPeriodDates();
      params.date_filter = LAST_ACTIVITY_DATE_FILTER;
      params.start_date = filters.value.start_date;
      params.end_date = filters.value.end_date;
    } else if (filters.value.activity_days) {
      params.activity_days = filters.value.activity_days;
    }
    if (filters.value.client_type === 'qualified') {
      params.qualification_status = 'qualificado';
    }
    if (filters.value.client_type === 'ready') {
      params.qualification_status = 'pronto';
    }

    const { data } = await LaisCrmContactsAPI.list(params);
    leads.value = data.payload || [];
    trackCrmEvent({
      accountId: accountId.value,
      name: 'crm_pipeline_viewed',
      properties: {
        activity_days: filters.value.activity_days,
        client_type: filters.value.client_type,
        start_date: filters.value.start_date,
        end_date: filters.value.end_date,
        leads_count: leads.value.length,
      },
    });
    trackCrmParityEvents({
      accountId: accountId.value,
      names: [
        'crm_home_page_viewed',
        'crm_charts_page_viewed',
        'my_clients_page_viewed',
      ],
      properties: {
        activity_days: filters.value.activity_days,
        client_type: filters.value.client_type,
        leads_count: leads.value.length,
      },
    });
  } catch (error) {
    hasError.value = true;
    useAlert(error.response?.data?.message || t('LEADS.PIPELINE.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleInlinePeriodChange = () => {
  handlePeriodSelection();
  fetchPipeline();
};

const handleInlineCustomDateChange = () => {
  normalizeCustomPeriodDates();
  fetchPipeline();
};

const openLead = lead => {
  selectedLead.value = lead;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'crm_quick_view_opened',
    properties: { contact_id: lead?.id },
  });
  trackCrmParityEvents({
    accountId: accountId.value,
    names: [
      'crm_home_openprofile_clicked',
      'crm_options_openprofile_clicked',
      'my_clients_card_opened',
    ],
    properties: { contact_id: lead?.id },
  });
};

const selectStage = key => {
  selectedStage.value = key;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'crm_pipeline_stage_opened',
    properties: { stage: key },
  });
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_charts_card_clicked', 'crm_home_quickfilter_clicked'],
    properties: { stage: key },
  });
};

const openPipelineSearch = () => {
  showSearch.value = true;
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_home_search_clicked'],
  });
};

const openPipelineFilters = () => {
  showFiltersDrawer.value = true;
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_home_filters_clicked', 'crm_filters_page_viewed'],
  });
};

const leadActivityTime = lead => {
  const value = leadStats(lead).lastActivity;
  const numeric = Number(value);
  const parsed = new Date(
    Number.isFinite(numeric) && numeric < 1_000_000_000_000
      ? numeric * 1000
      : value
  ).getTime();

  return Number.isNaN(parsed) ? 0 : parsed;
};

const leadSortName = lead =>
  `${lead?.name || lead?.contact?.name || ''}`
    .trim()
    .toLocaleLowerCase('pt-BR');

const sortedStageLeads = computed(() => {
  const rows = [...(activeStage.value?.leads || [])];

  if (drawerSortOrder.value === 'oldest_activity') {
    return rows.sort((a, b) => leadActivityTime(a) - leadActivityTime(b));
  }
  if (drawerSortOrder.value === 'name_asc') {
    return rows.sort((a, b) =>
      leadSortName(a).localeCompare(leadSortName(b), 'pt-BR')
    );
  }
  if (drawerSortOrder.value === 'name_desc') {
    return rows.sort((a, b) =>
      leadSortName(b).localeCompare(leadSortName(a), 'pt-BR')
    );
  }

  return rows.sort((a, b) => leadActivityTime(b) - leadActivityTime(a));
});

const updateDrawerSortOrder = value => {
  drawerSortOrder.value = value;
  trackCrmEvent({
    accountId: accountId.value,
    name: 'crm_pipeline_stage_sorted',
    properties: { stage: selectedStage.value, order: value },
  });
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_home_listorder_clicked'],
    properties: { stage: selectedStage.value, order: value },
  });
};

const resetFilters = () => {
  filters.value = defaultFilters();
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_home_clear_filters_clicked', 'crm_filters_cleared'],
  });
  fetchPipeline();
};

const applyDrawerFilters = () => {
  handlePeriodSelection();
  showFiltersDrawer.value = false;
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_filters_submitted'],
    properties: {
      activity_days: filters.value.activity_days,
      client_type: filters.value.client_type,
    },
  });
  fetchPipeline();
};

const clearDrawerFilters = () => {
  filters.value = defaultFilters();
  showFiltersDrawer.value = false;
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_filters_cleared'],
  });
  fetchPipeline();
};

const goToCapture = () => {
  router.push({
    name: 'lais_captacao_index',
    params: { accountId: accountId.value },
  });
};

const goToContacts = () => {
  router.push({
    name: 'contacts_dashboard_index',
    params: { accountId: accountId.value },
  });
};

const openHistory = lead => {
  if (!lead?.id) return;
  router.push(frontendURL(`accounts/${accountId.value}/history/${lead.id}`));
};

const leadPhone = lead =>
  lead?.phone_number ||
  lead?.phoneNumber ||
  lead?.phone ||
  lead?.contact?.phone_number ||
  lead?.contact?.phoneNumber ||
  '';

const closeQuickView = () => {
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_options_close_clicked'],
    properties: { contact_id: selectedLead.value?.id },
  });
  selectedLead.value = null;
};

const messageLead = lead => {
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_options_chat_clicked'],
    properties: { contact_id: lead?.id },
  });
  const conversationId =
    lead?.latest_conversation_id ||
    lead?.latestConversationId ||
    lead?.conversation_id ||
    lead?.conversation?.id;
  if (!conversationId) {
    useAlert('Este cliente ainda não tem conversa vinculada.');
    return;
  }
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversationId },
  });
};

const callLead = lead => {
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_options_call_clicked'],
    properties: { contact_id: lead?.id },
  });
  const phone = leadPhone(lead);
  if (!phone) {
    useAlert('Este cliente ainda não tem telefone cadastrado.');
    return;
  }
  window.location.href = `tel:${phone}`;
};

const scheduleVisit = lead => {
  if (!lead?.id) return;
  trackCrmParityEvents({
    accountId: accountId.value,
    names: ['crm_options_newvisit_clicked'],
    properties: { contact_id: lead.id },
  });
  router.push({
    name: 'visita_new',
    params: { accountId: accountId.value },
    query: { contact_id: String(lead.id) },
  });
};

const createReferral = lead => {
  router.push({
    name: 'lais_lead_referrals',
    params: { accountId: accountId.value },
    query: lead?.id ? { contact_id: String(lead.id) } : {},
  });
};

onMounted(fetchPipeline);
</script>

<template>
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      :title="t('LEADS.PIPELINE.TITLE')"
      :helper="t('LEADS.PIPELINE.HELPER')"
    >
      <template #actions>
        <button
          type="button"
          class="grid size-9 place-items-center rounded-full border border-lais-border-soft text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
          :aria-label="t('LEADS.SEARCH.TITLE')"
          @click="openPipelineSearch"
        >
          <span class="i-lucide-search size-4" aria-hidden="true" />
        </button>
        <LaisFilterButton active @click="openPipelineFilters" />
      </template>
    </LaisHeader>

    <div
      class="flex flex-wrap items-end gap-3 border-b border-lais-border-soft px-4 py-4 sm:px-6 lg:px-10"
    >
      <label class="grid gap-1">
        <span
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('LEADS.PIPELINE.PERIOD') }}
        </span>
        <select
          v-model="filters.activity_days"
          class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm text-lais-text"
          @change="handleInlinePeriodChange"
        >
          <option
            v-for="option in periodSelectOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label
        v-if="filters.activity_days === CUSTOM_ACTIVITY_PERIOD"
        class="grid gap-1"
      >
        <span
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('LEADS.PIPELINE.START_DATE') }}
        </span>
        <input
          v-model="filters.start_date"
          type="date"
          :min="customStartMinDate"
          :max="customStartMaxDate"
          class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm text-lais-text"
          @change="handleInlineCustomDateChange"
        />
      </label>
      <label
        v-if="filters.activity_days === CUSTOM_ACTIVITY_PERIOD"
        class="grid gap-1"
      >
        <span
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('LEADS.PIPELINE.END_DATE') }}
        </span>
        <input
          v-model="filters.end_date"
          type="date"
          :min="customEndMinDate"
          :max="todayDateKey"
          class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm text-lais-text"
          @change="handleInlineCustomDateChange"
        />
      </label>
      <label class="grid gap-1">
        <span
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('LEADS.PIPELINE.CLIENT_TYPE') }}
        </span>
        <select
          v-model="filters.client_type"
          class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm text-lais-text"
          @change="fetchPipeline"
        >
          <option value="">{{ t('LEADS.PIPELINE.ALL_CLIENTS') }}</option>
          <option value="qualified">{{ t('LEADS.PIPELINE.QUALIFIED') }}</option>
          <option value="ready">{{ t('LEADS.PIPELINE.READY') }}</option>
        </select>
      </label>
    </div>

    <div
      class="min-h-0 flex-1 overflow-auto px-4 py-5 sm:px-6 lg:px-10 max-md:flex-none max-md:overflow-visible"
    >
      <div v-if="isLoading && !leads.length" class="grid gap-4 md:grid-cols-5">
        <div
          v-for="stage in 5"
          :key="stage"
          class="h-64 animate-pulse rounded-lais border border-lais-border-soft bg-lais-bg-muted"
        />
      </div>

      <LaisCard v-else-if="hasError" density="tight">
        <div class="flex items-center justify-between gap-3">
          <p class="text-lais-body-sm text-lais-text">
            {{ t('LEADS.PIPELINE.ERROR') }}
          </p>
          <button
            type="button"
            class="rounded-lais bg-lais-primary px-4 py-2 text-lais-label-sm font-semibold text-white"
            @click="fetchPipeline"
          >
            {{ t('LEADS.PIPELINE.RETRY') }}
          </button>
        </div>
      </LaisCard>

      <EmptyStateLayout
        v-else-if="emptyPipeline"
        :title="t('LEADS.PIPELINE.EMPTY')"
        :subtitle="t('LEADS.PIPELINE.EMPTY_HELPER')"
        :show-backdrop="false"
      >
        <template #actions>
          <div class="flex flex-wrap justify-center gap-3">
            <LaisPrimaryButton icon="i-lucide-megaphone" @click="goToCapture">
              {{ t('LEADS.PIPELINE.EMPTY_CTA_CAPTURE') }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-upload"
              @click="goToContacts"
            >
              {{ t('LEADS.PIPELINE.EMPTY_CTA_IMPORT') }}
            </LaisPrimaryButton>
          </div>
        </template>
      </EmptyStateLayout>

      <LaisCard v-else-if="emptyFiltered" density="tight">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-lais-body-sm text-lais-text-muted">
            {{ t('LEADS.PIPELINE.EMPTY_FILTERED') }}
          </p>
          <LaisPrimaryButton variant="ghost" size="sm" @click="resetFilters">
            {{ t('LEADS.PIPELINE.CLEAR_FILTERS') }}
          </LaisPrimaryButton>
        </div>
      </LaisCard>

      <div v-else class="grid gap-5">
        <LaisCard :title="t('LEADS.PIPELINE.DISTRIBUTION')" density="tight">
          <div class="flex h-3 overflow-hidden rounded-full bg-lais-bg-muted">
            <div
              v-for="bucket in stageBuckets"
              :key="bucket.key"
              :class="bucket.colorClass"
              :style="{ width: `${bucket.percent}%` }"
            />
          </div>
          <div class="mt-4 flex flex-wrap gap-3">
            <button
              v-for="bucket in stageBuckets"
              :key="bucket.key"
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-lais-border-soft px-3 py-1.5 text-lais-label-sm text-lais-text"
              @click="selectStage(bucket.key)"
            >
              <span class="size-2 rounded-full" :class="bucket.colorClass" />
              {{ stageLabel(bucket.key) }}
              <strong>{{ bucket.count }}</strong>
            </button>
          </div>
        </LaisCard>

        <div class="hidden grid-cols-5 gap-3 xl:grid">
          <section
            v-for="bucket in stageBuckets"
            :key="bucket.key"
            class="min-w-0 rounded-lais border border-lais-border-soft bg-lais-bg-card"
          >
            <header class="border-b border-lais-border-soft p-3">
              <div class="flex items-center justify-between gap-2">
                <h2
                  class="font-redHatDisplay text-lais-heading-xs font-semibold text-lais-text"
                >
                  {{ stageLabel(bucket.key) }}
                </h2>
                <StatusTag
                  :label="
                    t('LEADS.PIPELINE.COUNT', bucket.count, { n: bucket.count })
                  "
                  :variant="bucket.variant"
                  size="xs"
                />
              </div>
              <p class="mt-1 text-lais-body-xs text-lais-text-muted">
                {{ stageDescription(bucket.key) }}
              </p>
            </header>
            <div class="grid max-h-[60vh] gap-2 overflow-auto p-3">
              <button
                v-for="lead in bucket.leads"
                :key="lead.id"
                type="button"
                class="rounded-lais border border-lais-border-soft bg-lais-bg px-3 py-3 text-left transition hover:border-lais-primary"
                @click="openLead(lead)"
              >
                <div class="flex items-start gap-2">
                  <span
                    class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 font-redHatDisplay text-lais-label-sm font-semibold text-lais-primary"
                  >
                    {{ leadInitials(lead) }}
                  </span>
                  <span class="min-w-0">
                    <span class="block truncate font-semibold text-lais-text">
                      {{ lead.name || t('LEADS.QUICK_VIEW.ANONYMOUS') }}
                    </span>
                    <span
                      class="block truncate text-lais-body-xs text-lais-text-muted"
                    >
                      {{
                        leadContactLine(lead) ||
                        t('LEADS.QUICK_VIEW.NO_CONTACT')
                      }}
                    </span>
                  </span>
                </div>
                <div
                  class="mt-3 flex items-center justify-between text-lais-body-xs text-lais-text-muted"
                >
                  <span>
                    {{ leadStats(lead).visits }}
                    {{ t('LEADS.QUICK_VIEW.VISITS') }}
                  </span>
                  <span>{{
                    formatDate(leadStats(lead).lastActivity) ||
                    t('LEADS.QUICK_VIEW.NO_ACTIVITY')
                  }}</span>
                </div>
              </button>
            </div>
          </section>
        </div>

        <div class="grid gap-2 xl:hidden">
          <button
            v-for="bucket in stageBuckets"
            :key="bucket.key"
            type="button"
            class="flex items-center justify-between rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-left"
            @click="selectStage(bucket.key)"
          >
            <span>
              <span class="block font-semibold text-lais-text">{{
                stageLabel(bucket.key)
              }}</span>
              <span class="text-lais-body-xs text-lais-text-muted">{{
                stageDescription(bucket.key)
              }}</span>
            </span>
            <StatusTag
              :label="
                t('LEADS.PIPELINE.COUNT', bucket.count, { n: bucket.count })
              "
              :variant="bucket.variant"
            />
          </button>
        </div>
      </div>
    </div>

    <LaisDrawer
      :show="!!selectedStage"
      :title="activeStage ? stageLabel(activeStage.key) : ''"
      side="bottom"
      size="lg"
      @close="selectedStage = null"
    >
      <template #body>
        <div class="grid gap-4">
          <div
            class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
          >
            <div
              class="mb-3 flex h-2 overflow-hidden rounded-full bg-lais-bg-muted"
            >
              <div
                v-for="bucket in stageBuckets"
                :key="`drawer-bar-${bucket.key}`"
                :class="bucket.colorClass"
                :style="{ width: `${bucket.percent}%` }"
              />
            </div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="bucket in stageBuckets"
                :key="`drawer-caption-${bucket.key}`"
                class="inline-flex items-center gap-1.5 text-lais-label-xs font-semibold text-lais-text-muted"
              >
                <span class="size-2 rounded-full" :class="bucket.colorClass" />
                {{ stageLabel(bucket.key) }}
              </span>
            </div>
          </div>

          <div
            class="rounded-lais border border-lais-primary/20 bg-lais-primary-50 px-4 py-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p
                  class="text-lais-label-sm font-semibold uppercase text-lais-primary"
                >
                  {{ t('LEADS.PIPELINE.STAGE_TIP') }}
                </p>
                <p class="mt-1 text-lais-body-sm text-lais-text">
                  {{ activeStage ? stageTip(activeStage.key) : '' }}
                </p>
              </div>
              <StatusTag
                v-if="activeStage"
                :label="stageLabel(activeStage.key)"
                :variant="activeStage.variant"
                size="xs"
              />
            </div>
          </div>

          <div class="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p
                class="font-redHatDisplay text-lais-heading-xs font-semibold text-lais-text"
              >
                {{
                  t('LEADS.PIPELINE.COUNT_TITLE', activeStage?.count || 0, {
                    n: activeStage?.count || 0,
                  })
                }}
              </p>
              <p class="text-lais-body-xs text-lais-text-muted">
                {{ activeStage ? stageDescription(activeStage.key) : '' }}
              </p>
            </div>
            <label class="grid min-w-[220px] gap-1">
              <span
                class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
              >
                {{ t('LEADS.PIPELINE.ORDER.LABEL') }}
              </span>
              <LaisSelect
                :model-value="drawerSortOrder"
                :options="stageSortOptions"
                :placeholder="t('LEADS.PIPELINE.ORDER.LABEL')"
                @update:model-value="updateDrawerSortOrder"
              />
            </label>
          </div>
          <div class="grid gap-2">
            <button
              v-for="lead in sortedStageLeads"
              :key="lead.id"
              type="button"
              class="rounded-lais border border-lais-border-soft p-3 text-left transition hover:border-lais-primary"
              @click="openLead(lead)"
            >
              <span class="font-semibold text-lais-text">
                {{ lead.name || t('LEADS.QUICK_VIEW.ANONYMOUS') }}
              </span>
              <span class="block text-lais-body-xs text-lais-text-muted">
                {{ leadContactLine(lead) || t('LEADS.QUICK_VIEW.NO_CONTACT') }}
              </span>
            </button>
          </div>
        </div>
      </template>
    </LaisDrawer>

    <CrmFiltersDrawer
      :show="showFiltersDrawer"
      @close="showFiltersDrawer = false"
      @apply="applyDrawerFilters"
      @clear="clearDrawerFilters"
    >
      <label class="grid gap-1">
        <span
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('LEADS.PIPELINE.PERIOD') }}
        </span>
        <select
          v-model="filters.activity_days"
          class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm"
          @change="handlePeriodSelection"
        >
          <option
            v-for="option in periodSelectOptions"
            :key="`drawer-${option.value}`"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <div
        v-if="filters.activity_days === CUSTOM_ACTIVITY_PERIOD"
        class="grid gap-3 sm:grid-cols-2"
      >
        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('LEADS.PIPELINE.START_DATE') }}
          </span>
          <input
            v-model="filters.start_date"
            type="date"
            :min="customStartMinDate"
            :max="customStartMaxDate"
            class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm"
            @change="normalizeCustomPeriodDates"
          />
        </label>
        <label class="grid gap-1">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('LEADS.PIPELINE.END_DATE') }}
          </span>
          <input
            v-model="filters.end_date"
            type="date"
            :min="customEndMinDate"
            :max="todayDateKey"
            class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm"
            @change="normalizeCustomPeriodDates"
          />
        </label>
      </div>
      <label class="grid gap-1">
        <span
          class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
        >
          {{ t('LEADS.PIPELINE.CLIENT_TYPE') }}
        </span>
        <select
          v-model="filters.client_type"
          class="h-10 rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm"
        >
          <option value="">{{ t('LEADS.PIPELINE.ALL_CLIENTS') }}</option>
          <option value="qualified">{{ t('LEADS.PIPELINE.QUALIFIED') }}</option>
          <option value="ready">{{ t('LEADS.PIPELINE.READY') }}</option>
        </select>
      </label>
    </CrmFiltersDrawer>

    <CrmSearchDialog
      :show="showSearch"
      @close="showSearch = false"
      @open-lead="openLead"
    />

    <LeadQuickView
      :show="!!selectedLead"
      :lead="selectedLead"
      @close="closeQuickView"
      @message="messageLead"
      @call="callLead"
      @open-history="openHistory"
      @schedule-visit="scheduleVisit"
      @create-referral="createReferral"
    />
  </section>
</template>
